'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { LegislationVersionDatePicker } from '@/components/ui/legislation-version-date-picker';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Link } from '@/i18n/routing';
import { SlovLexUrl } from '@/lib/config';
import { parseFragmentToLabel } from '@/lib/utils/legalReferenceLabel';
import { BADGE_STYLES, SIDEBAR_STYLES } from '@/lib/utils/metadataStyles';
import type {
  LegislationInterface,
  LegislationRelationshipInterface,
} from '@/types/api-types';
import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { useLocale, useTranslations } from 'next-intl';
import ViewModeToggle from './ViewModeToggle';

interface LegislationMetadataProps {
  legislationInfo: LegislationInterface;
  iri?: string | null;
  documentId?: number;
  originalSource?: string | null | undefined;
  // PDF download/view URL — render the View toggle when present.
  pdfUrl?: string | null;
  // True when the backend returned `urls.markdown_text` for this document.
  hasMarkdown?: boolean;
  viewMode?: 'html' | 'pdf' | 'markdown';
  onViewModeChange?: (mode: 'html' | 'pdf' | 'markdown') => void;
}

/**
 * Format date to human-readable format based on locale
 */
function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  const localeMap: Record<string, string> = {
    sk: 'sk-SK',
    en: 'en-US',
  };
  return new Intl.DateTimeFormat(localeMap[locale] || 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Transform URL from slov-lex format to internal format
 */
function transformInstrumentUrl(url: string): string {
  if (!url) return '#';

  const regex = /\/ezbierky-fe\/pravne-predpisy\/(SK\/.*)/;
  const match = url.match(regex);

  if (match && match[1]) {
    return `/document/${match[1]}`;
  }

  return url;
}

/**
 * Build a display label for a regulation, incorporating the URL fragment
 * into proper Slovak legal citation format.
 *
 * E.g. "§ 421 ods. 1 písm. b) zákona č. 160/2015 Z.z."
 */
function formatRegulationLabel(regulationNumber: string, link: string): string {
  const fragment = parseFragmentToLabel(link);
  if (!fragment) return regulationNumber;
  return `${fragment} ${regulationNumber}`;
}

/**
 * Create href for instrument links
 */
function createInstrumentHref(url: string) {
  const transformedUrl = transformInstrumentUrl(url);
  return {
    pathname: '/document/[...segments]' as const,
    params: {
      segments: transformedUrl.replace('/document/', '').split('/'),
    },
  };
}

/**
 * LegislationRelationships sub-component - purple styled accordion
 */
function LegislationRelationshipsSection({
  legislationRelationship,
}: {
  legislationRelationship: LegislationRelationshipInterface;
}) {
  if (!legislationRelationship?.sections?.length) {
    return null;
  }

  return (
    <Accordion type='single' collapsible className='w-full space-y-2'>
      {legislationRelationship.sections.map((section, sectionIndex) => (
        <AccordionItem
          key={`section-${sectionIndex}`}
          value={`section-${sectionIndex}`}
          className='border border-brand-primary-color/20 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200'
        >
          <AccordionTrigger className='text-sm font-medium py-2 px-3 bg-brand-primary-color/5 hover:bg-brand-primary-color/10 text-left text-brand-primary-color [&[data-state=open]]:bg-brand-primary-color/10 [&>svg]:text-brand-primary-color/60'>
            <span className='break-words pr-2'>{section.title}</span>
          </AccordionTrigger>
          <AccordionContent className='px-4 pb-4'>
            <div className='space-y-2 pt-2'>
              {section.regulations.map((regulation, regulationIndex) => (
                <Link
                  key={`regulation-${regulationIndex}`}
                  href={createInstrumentHref(regulation.link)}
                  className='block p-3 bg-brand-primary-color/5 rounded-lg hover:bg-brand-primary-color/10 transition-colors overflow-hidden group'
                >
                  <span className='text-base text-brand-primary-color font-medium group-hover:underline break-words'>
                    {formatRegulationLabel(
                      regulation.regulation_number,
                      regulation.link
                    )}
                  </span>
                  <p className='mt-1 text-sm text-content-muted line-clamp-2 break-words'>
                    {regulation.description}
                  </p>
                </Link>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

/**
 * LegislationMetadata - Displays legislation metadata in a sidebar card
 *
 * Shows:
 * - Legislation name and regulation number
 * - Key dates (approval, declared, effective from/till)
 * - Author and type
 * - Legislation areas
 * - Version history
 * - Related regulations
 * - Link to original on Slov-lex
 */
export default function LegislationMetadata({
  legislationInfo,
  iri: iriProp,
  documentId,
  originalSource,
  pdfUrl,
  hasMarkdown = false,
  viewMode,
  onViewModeChange,
}: LegislationMetadataProps) {
  const t = useTranslations('LegislationDetails');
  const locale = useLocale();

  // Use iri from props (passed from documentInfo), fallback to legislationInfo.iri
  const rawIri = iriProp || legislationInfo.iri;
  // Strip leading slash if present for URL construction
  const iri = rawIri?.startsWith('/') ? rawIri.substring(1) : rawIri;

  // Format dates
  const dateOfApproval = legislationInfo.date_of_approval
    ? formatDate(legislationInfo.date_of_approval, locale)
    : null;
  const declaredDate = legislationInfo.declared
    ? formatDate(legislationInfo.declared, locale)
    : null;
  const effectiveFromDate = legislationInfo.effective_from
    ? formatDate(legislationInfo.effective_from, locale)
    : null;
  const effectiveTillDate = legislationInfo.effective_till
    ? formatDate(legislationInfo.effective_till, locale)
    : null;

  return (
    <div className={SIDEBAR_STYLES.container}>
      {/* Header Section */}
      <div className={SIDEBAR_STYLES.header}>
        <div className={SIDEBAR_STYLES.titleRow}>
          <h2 data-testid='document-title' className={SIDEBAR_STYLES.title}>
            {legislationInfo.regulation_number}
          </h2>
          {(originalSource || iri) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    className='inline-flex items-center justify-center h-7 w-7 bg-brand-primary-color/10 hover:bg-brand-primary-color/20 rounded-lg transition-all duration-200 text-brand-primary-color'
                    onClick={() => {
                      window.open(
                        originalSource || `${SlovLexUrl}/${iri}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                    aria-label={t('viewOriginal')}
                  >
                    <ExternalLinkIcon className='h-4 w-4' />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('viewOriginal')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {legislationInfo.legislation_type && (
            <Badge variant='secondary' className={BADGE_STYLES.legislation}>
              {legislationInfo.legislation_type}
            </Badge>
          )}
        </div>
        <p className={SIDEBAR_STYLES.description}>{legislationInfo.name}</p>
      </div>

      {pdfUrl && viewMode && onViewModeChange && (
        <ViewModeToggle
          pdfUrl={pdfUrl}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          hasMarkdown={hasMarkdown}
        />
      )}

      {/* Main Sections */}
      <div className={SIDEBAR_STYLES.sectionsContainer}>
        {/* Key Dates Table */}
        {(dateOfApproval ||
          declaredDate ||
          effectiveFromDate ||
          effectiveTillDate) && (
          <div className={SIDEBAR_STYLES.dateTable}>
            <div className={SIDEBAR_STYLES.dateTableInner}>
              {dateOfApproval && (
                <div className={SIDEBAR_STYLES.dateRow}>
                  <div className={SIDEBAR_STYLES.dateLabelCell}>
                    {t('dateOfApproval')}
                  </div>
                  <div className={SIDEBAR_STYLES.dateValueCell}>
                    {dateOfApproval}
                  </div>
                </div>
              )}
              {declaredDate && (
                <div className={SIDEBAR_STYLES.dateRow}>
                  <div className={SIDEBAR_STYLES.dateLabelCell}>
                    {t('issueDate')}
                  </div>
                  <div className={SIDEBAR_STYLES.dateValueCell}>
                    {declaredDate}
                  </div>
                </div>
              )}
              {effectiveFromDate && (
                <div className={SIDEBAR_STYLES.dateRow}>
                  <div className={SIDEBAR_STYLES.dateLabelCell}>
                    {t('effectiveFrom')}
                  </div>
                  <div className={SIDEBAR_STYLES.dateValueCell}>
                    {effectiveFromDate}
                  </div>
                </div>
              )}
              {effectiveTillDate && (
                <div className={SIDEBAR_STYLES.dateRow}>
                  <div className={SIDEBAR_STYLES.dateLabelCell}>
                    {t('effectiveTill')}
                  </div>
                  <div className={SIDEBAR_STYLES.dateValueCell}>
                    {effectiveTillDate}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Time Version Selector */}
        {iri && documentId && (
          <LegislationVersionDatePicker iri={iri} documentId={documentId} />
        )}

        {/* Author Section */}
        {legislationInfo.author && (
          <div className={SIDEBAR_STYLES.itemContainer}>
            <h3 className={SIDEBAR_STYLES.itemLabel}>{t('author')}</h3>
            <p className={SIDEBAR_STYLES.itemValue}>{legislationInfo.author}</p>
          </div>
        )}

        {/* Legal Areas Section */}
        {legislationInfo.legislation_areas &&
          legislationInfo.legislation_areas.length > 0 && (
            <div className={SIDEBAR_STYLES.section}>
              <h3 className={SIDEBAR_STYLES.sectionLabel}>{t('areas')}</h3>
              <div className={SIDEBAR_STYLES.badgeList}>
                {legislationInfo.legislation_areas.map((item, index) => (
                  <Badge
                    key={index}
                    variant='secondary'
                    className={BADGE_STYLES.area}
                  >
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}

        {/* Related Regulations Section */}
        {legislationInfo.legislation_relationship &&
          legislationInfo.legislation_relationship.sections?.length > 0 && (
            <div className={SIDEBAR_STYLES.section}>
              <h3 className={SIDEBAR_STYLES.sectionLabel}>
                {t('relatedRegulations')}
              </h3>
              <LegislationRelationshipsSection
                legislationRelationship={
                  legislationInfo.legislation_relationship
                }
              />
            </div>
          )}
      </div>
    </div>
  );
}
